import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { StationMarkerCreate, StationMarkerPatch, StationSlug } from '../types'

// Per-event station-map markers (spec 31 / item 7). Cache key includes
// the station filter so switching tabs doesn't clobber the cached set
// for the previous station. Mutations invalidate both the filtered and
// unfiltered cache keys for the event so any open list refetches.

export function useStationMarkers(eventId: number, station?: StationSlug) {
  return useQuery({
    queryKey: ['station-markers', eventId, station ?? 'all'],
    queryFn: () => ApiClient.stationMarkers(eventId, station),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useCreateStationMarker(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StationMarkerCreate) => ApiClient.createStationMarker(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['station-markers', eventId] })
  })
}

export function usePatchStationMarker(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: StationMarkerPatch }) =>
      ApiClient.patchStationMarker(eventId, id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['station-markers', eventId] })
  })
}

export function useDeleteStationMarker(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteStationMarker(eventId, id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['station-markers', eventId] })
  })
}
